import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Icon from '../Icon';

// Utility function to convert millimeters to inches
function convertMmToInches(lengthStr) {
  const mmPerInch = 25.4;

  // Check if the string represents a length measurement in millimeters
  if (isLengthMeasurement(lengthStr)) {
    // Extract the numeric part of the measurement
    const match = lengthStr.match(/(\d+(\.\d+)?)\s*mm/);
    if (match && match[1]) {
      const lengthInMm = parseFloat(match[1]);
      return (lengthInMm / mmPerInch).toFixed(2) + ' in';
    } else {
      return lengthStr; // Return original string if no valid number found
    }
  } else {
    return lengthStr; // Return original string if it's not a length measurement
  }
}

// Utility function to convert inches to millimeters
function convertInchesToMm(lengthStr) {
  const mmPerInch = 25.4;

  // Check if the string represents a length measurement in inches
  const inchPattern = /(\d+(\.\d+)?)\s*in/;
  const match = lengthStr.match(inchPattern);

  if (match && match[1]) {
    const lengthInInches = parseFloat(match[1]);
    return (lengthInInches * mmPerInch).toFixed(2) + ' mm';
  } else {
    return lengthStr; // Return original string if no valid number found
  }
}

function isLengthMeasurement(line) {
  // Regular expression to match a pattern with a number followed by 'mm'
  const mmPattern = /\d+(\.\d+)?\s*mm/;
  return mmPattern.test(line);
}

function isInchMeasurement(line) {
  // Regular expression to match a pattern with a number followed by 'in'
  const inPattern = /\d+(\.\d+)?\s*in/;
  return inPattern.test(line);
}

const MeasurementItem = ({ uid, index, label, displayText, isActive, onClick, onEdit, item }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [measurementUnit, setMeasurementUnit] = useState('inches');

  // Load the measurement unit preference when the component mounts
  useEffect(() => {
    try {
      const savedUnit = localStorage.getItem('ohif-measurement-unit');
      if (savedUnit) {
        setMeasurementUnit(savedUnit);
      }
    } catch (error) {
      console.warn('Error loading measurement unit preference:', error);
    }
  }, []);

  const onEditHandler = event => {
    event.stopPropagation();
    onEdit({ uid, isActive, event });
  };

  const onClickHandler = event => onClick({ uid, isActive, event });

  const onMouseEnter = () => setIsHovering(true);
  const onMouseLeave = () => setIsHovering(false);

  // Convert measurements based on the user preference
  const convertMeasurement = line => {
    if (measurementUnit === 'mm') {
      // If the preference is mm and the measurement is in inches, convert to mm
      if (isInchMeasurement(line)) {
        return convertInchesToMm(line);
      }
      return line; // Already in mm or not a measurement
    } else {
      // If the preference is inches and the measurement is in mm, convert to inches
      if (isLengthMeasurement(line)) {
        return convertMmToInches(line);
      }
      return line; // Already in inches or not a measurement
    }
  };

  console.log('LisaA displayText', displayText);

  return (
    <div
      className={classnames(
        'group flex cursor-pointer border border-transparent bg-black outline-none transition duration-300',
        {
          'border-primary-light overflow-hidden rounded': isActive,
        }
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClickHandler}
      role="button"
      tabIndex={0}
      data-cy={'measurement-item'}
    >
      <div
        className={classnames('w-6 py-1 text-center text-base transition duration-300', {
          'bg-orange-1 active text-black': isActive,
          'border-orange-1 text-white group-hover:bg-orange-700': !isActive,
        })}
      >
        {index}
      </div>
      <div className="relative flex flex-1 flex-col px-2 py-1">
        <span className="text-primary-light mb-1 text-base">{label}</span>
        {displayText.map((line, i) => {
          // Convert measurements based on user preference
          const convertedLine = convertMeasurement(line);
          return (
            <span
              key={i}
              className="border-primary-light border-l pl-2 text-base text-white"
              dangerouslySetInnerHTML={{ __html: convertedLine }}
            ></span>
          );
        })}

        <Icon
          className={classnames(
            'absolute w-4 cursor-pointer text-white transition duration-300',
            { 'invisible mr-2 opacity-0': !isActive && !isHovering },
            { 'opacity-1 visible': !isActive && isHovering }
          )}
          name="pencil"
          style={{
            top: 4,
            right: 4,
            transform: isActive || isHovering ? '' : 'translateX(100%)',
          }}
          onClick={onEditHandler}
        />
      </div>
    </div>
  );
};

MeasurementItem.propTypes = {
  uid: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
  index: PropTypes.number.isRequired,
  label: PropTypes.string,
  displayText: PropTypes.array.isRequired,
  isActive: PropTypes.bool,
  onClick: PropTypes.func,
  onEdit: PropTypes.func,
  item: PropTypes.object,
};

MeasurementItem.defaultProps = {
  isActive: false,
};

export default MeasurementItem;
